import React, { useEffect, useState, useRef } from 'react';
import { Post } from '../../types/Post';
import { getMonsterImgPath } from '../../utils/monster';

import ProfileWithName from '../../components/ProfileWithName';
import { monthDayTime } from '../../utils/date';
import MonsterSelect from './MonsterSelect';
import { isOperator } from '../../utils/permission';
import { IconLink } from '../../components/Icons';
import { loadMonsterMapping } from '../../utils/monsterMapping';
import { TacticBuild, BuildRunes, hasBuild } from '../TacticBuild';
import { openRuneFit } from '../../utils/fitHandoff';
import { useBackHandler } from '../../hooks/useBackHandler';
import ExtraDefenseRows, { ImportDefenseButton } from '../Common/ExtraDefenses';

interface Monster {
  name: string;
  attribute: string;
}

interface TacticPostProps {
  open: boolean;
  onClose: () => void;
  tactic: Post | null;
  monsterMapping: Record<string, any>;
  user: { uuid: string; nickname: string; role: string; profileImg: string };
  onTacticDeleted?: () => void;
}

const TacticPost: React.FC<TacticPostProps> = ({ open, onClose, tactic: initialTactic, monsterMapping: initialMonsterMapping, user, onTacticDeleted }) => {
  const [tactic, setTactic] = useState<Post | null>(initialTactic);
  /**
   * [공략] / [룬 정보] 탭. 룬 정보는 공덱 몹을 누르면 그 몹의 세팅을 전투기록 창처럼 보여 준다(사용자 지시).
   * 방덱은 룬 세팅을 적지 않는다.
   */
  const [tab, setTab] = useState<'tactic' | 'runes'>('tactic');
  const [runeIdx, setRuneIdx] = useState(0);
  /** 수정 화면의 공덱 세 칸 세팅 — 몹을 바꾸거나 빼면 그 칸은 지운다 */
  const [editBuilds, setEditBuilds] = useState<Array<TacticBuild | null>>([null, null, null]);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [userDropdownOpen, setUserDropdownOpen] = useState(false);
  const userDropdownRef = useRef<HTMLDivElement>(null);
  const [editMode, setEditMode] = useState(false);
  const [editTitle, setEditTitle] = useState('');
  const [editComment, setEditComment] = useState('');
  /** 수정 중인 호환 방덱 — [저장] 할 때 함께 보낸다 */
  const [editExtra, setEditExtra] = useState<any[]>([]);
  const [editAttackTeam, setEditAttackTeam] = useState<Monster[]>([]);
  const [editDefenseTeam, setEditDefenseTeam] = useState<Monster[]>([]);
  const [editLoading, setEditLoading] = useState(false);
  const [editError, setEditError] = useState('');
  const [monsterSelectOpen, setMonsterSelectOpen] = useState(false);
  const [editingTeam, setEditingTeam] = useState<'attack' | 'defense' | 'extra' | null>(null);
  /** 추가 방덱 줄 중 몇 번째를 고르는 중인지 */
  const [editingRow, setEditingRow] = useState(0);
  const [editingSlot, setEditingSlot] = useState<number>(0);
  const [monsterMapping, setMonsterMapping] = useState<Record<string, any>>(initialMonsterMapping || {});
  const [mappingLoaded, setMappingLoaded] = useState(false);


  // 모달 닫기 핸들러 (드롭다운도 함께 닫음)
  const handleClose = () => {
    setDropdownOpen(false);
    setEditMode(false);
    onClose();
  };

  // 몬스터 매핑 로드
  useEffect(() => {
    if (open) {
      // 이미 매핑이 있으면 로드된 것으로 간주
      if (initialMonsterMapping && Object.keys(initialMonsterMapping).length > 0 && initialMonsterMapping.monsters) {
        setMonsterMapping(initialMonsterMapping);
        setMappingLoaded(true);
      } else if (!mappingLoaded) {
        // 매핑이 없으면 API에서 로드
        loadMonsterMapping()
          .then(data => {
            setMonsterMapping(data);
            setMappingLoaded(true);
          })
          .catch(error => {
            setMappingLoaded(true); // 에러가 나도 계속 진행
          });
      }
    } else {
      setMappingLoaded(false);
    }
  }, [open, initialMonsterMapping]);

  // 최신 공략 데이터 fetch
  useEffect(() => {
    if (open && initialTactic?._id) {
      fetch(`/api/tactics/${initialTactic._id}`, { credentials: 'include' })
        .then(res => res.json())
        .then(data => {
          setTactic(data);
        });
    } else {
      setTactic(initialTactic);
      setDropdownOpen(false);
      setEditMode(false);
    }
  }, [open, initialTactic]);


  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setDropdownOpen(false);
      }
    }
    if (dropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [dropdownOpen]);

  // 유저 드롭다운 외부 클릭 핸들러
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (userDropdownRef.current && !userDropdownRef.current.contains(event.target as Node)) {
        setUserDropdownOpen(false);
      }
    }
    if (userDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [userDropdownOpen]);


  // 수정 버튼 클릭 시
  const handleEditClick = () => {
    setEditTitle(tactic?.title || '');
    setEditComment(tactic?.comment || '');
    setEditExtra((tactic as any)?.extraDefenses || []);
    // 3개씩 패딩
    const attack = [...(tactic?.attackTeam || [])];
    const defense = [...(tactic?.defenseTeam || [])];
    while (attack.length < 3) attack.push({ name: '', attribute: '' });
    while (defense.length < 3) defense.push({ name: '', attribute: '' });
    setEditAttackTeam(attack);
    setEditDefenseTeam(defense);
    setEditBuilds([0, 1, 2].map((i) => (tactic?.builds?.attack?.[i] as TacticBuild) || null));
    setEditMode(true);
    setDropdownOpen(false);
  };

  // 몬스터 선택 핸들러
  const handleMonsterSelect = (monster: any) => {
    const newMonster = { name: monster.name, attribute: monster.attribute };
    if (editingTeam === 'attack') {
      const newTeam = [...editAttackTeam];
      newTeam[editingSlot] = newMonster;
      setEditAttackTeam(newTeam);
      setEditBuilds((bs) => bs.map((b, i) => (i === editingSlot ? null : b)));
    } else if (editingTeam === 'extra') {
      setEditExtra((rows) => rows.map((r, i) => (i === editingRow ? r.map((m: any, j: number) => (j === editingSlot ? newMonster : m)) : r)));
    } else if (editingTeam === 'defense') {
      const newTeam = [...editDefenseTeam];
      newTeam[editingSlot] = newMonster;
      setEditDefenseTeam(newTeam);
    }
    setMonsterSelectOpen(false);
  };

  // 몬스터 삭제 핸들러
  const handleMonsterRemove = (team: 'attack' | 'defense', index: number) => {
    if (team === 'attack') {
      const newTeam = [...editAttackTeam];
      newTeam[index] = { name: '', attribute: '' };
      setEditAttackTeam(newTeam);
      setEditBuilds((bs) => bs.map((b, i) => (i === index ? null : b)));
    } else {
      const newTeam = [...editDefenseTeam];
      newTeam[index] = { name: '', attribute: '' };
      setEditDefenseTeam(newTeam);
    }
  };

  // 저장 버튼 클릭 시
  /**
   * 수정한 공덱 세팅 — 빈 칸을 걸러 내면 칸이 당겨지므로 **몹과 짝을 지은 채로** 거른다.
   * 방덱은 적지 않는다.
   */
  const editedBuilds = () => {
    const kept = editAttackTeam
      .map((m, i) => ({ m, b: editBuilds[i] || null }))
      .filter((x) => x.m.name && x.m.attribute)
      .map((x) => x.b);
    return { attack: [0, 1, 2].map((i) => kept[i] || null) };
  };

  const handleEditSave = async () => {
    if (!editTitle.trim()) {
      setEditError('제목을 입력하세요.');
      return;
    }
    // 빈 몬스터 제거
    const attackFiltered = editAttackTeam.filter(m => m.name && m.attribute);
    const defenseFiltered = editDefenseTeam.filter(m => m.name && m.attribute);
    setEditLoading(true);
    setEditError('');
    try {
      const res = await fetch(`/api/tactics/${tactic?._id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          title: editTitle,
          comment: editComment,
          // 셋이 다 찬 줄만
          extraDefenses: editExtra.filter((r) => r.every((m: any) => m.name && m.attribute)),
          attackTeam: attackFiltered,
          defenseTeam: defenseFiltered,
          builds: editedBuilds()
        })
      });
      const data = await res.json();
      if (data.success) {
        setTactic(data.tactic);
        setEditMode(false);
      } else {
        setEditError(data.error || '수정 실패');
      }
    } catch (e) {
      setEditError('수정 중 오류 발생');
    }
    setEditLoading(false);
  };

  // 공략 삭제 핸들러
  const handleDeleteTactic = async () => {
    if (!tactic?._id || !window.confirm('정말로 이 공략을 삭제하시겠습니까?')) return;

    try {
      const res = await fetch(`/api/tactics/${tactic._id}`, {
        method: 'DELETE',
        credentials: 'include',
      });

      if (res.ok) {
        alert('공략이 삭제되었습니다.');
        handleClose(); // 모달 닫기 (드롭다운도 함께 닫음)
        // 부모 컴포넌트에 삭제 완료 알림
        if (onTacticDeleted) {
          onTacticDeleted();
        }
      } else {
        const data = await res.json();
        alert(data.message || '삭제에 실패했습니다.');
      }
    } catch (error) {
      alert('삭제 중 오류가 발생했습니다.');
    }
  };

  // 뒤로 가기: 몹 고르기 창 → 수정 모드 순으로 물린다 (공략 창 자체는 연 쪽이 등록한다)
  useBackHandler(open && editMode, () => { setEditMode(false); setEditError(''); });
  useBackHandler(open && monsterSelectOpen, () => setMonsterSelectOpen(false));

  if (!open || !tactic) return null;
  const date = new Date(tactic.createdAt);
  const now = new Date();
  const isToday = date.getFullYear() === now.getFullYear() && date.getMonth() === now.getMonth() && date.getDate() === now.getDate();
  const dateStr = isToday
    ? `${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}`
    : date.toLocaleDateString('ko-KR', { year: 'numeric', month: '2-digit', day: '2-digit' }).replace(/\./g, '.').replace(/\s/g, '').replace(/\.$/, '');
  const isOwner = user.nickname === tactic.nickname;
  const isAdmin = isOperator(user);
  // 내 룬으로 맞추기 — 이 공략 공덱 룬작에 내 룬을 룬 옵티마이저 [룬피팅] 탭에서 맞춘다 (사용자 2026-10-27). [공략] 탭에도 (사용자 2026-10-28)
  const fitButton = (tactic.attackTeam || []).length === 3 && [0, 1, 2].some((i) => hasBuild(tactic.builds?.attack?.[i] as TacticBuild)) ? (
    <button
      type="button"
      onClick={() => {
        openRuneFit({ label: `공략 「${tactic.title || '제목 없음'}」 공덱`, mobs: (tactic.attackTeam || []).slice(0, 3), builds: [0, 1, 2].map((i) => (tactic.builds?.attack?.[i] as TacticBuild) || null) });
        onClose();
      }}
      style={{ marginTop: 10, width: '100%', padding: '12px 0', borderRadius: 12, border: '1.5px solid rgba(102, 126, 234, 0.6)', background: 'rgba(102, 126, 234, 0.14)', color: '#667eea', fontWeight: 800, fontSize: 15, cursor: 'pointer' }}
    >내 룬으로 맞추기</button>
  ) : null;

  return (
    <div style={{
      position: 'fixed',
      left: 0,
      top: 0,
      width: '100vw',
      height: '100vh',
      zIndex: 3000,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backdropFilter: 'blur(10px)',
      background: 'rgba(0, 0, 0, 0.5)',
      padding: '20px'
    }}>

      <div style={{
        background: '#fff',
        borderRadius: 24,
        minWidth: 480,
        maxWidth: 680,
        width: '90vw',
        padding: '72px 48px 48px',
        boxShadow: '0 20px 60px rgba(0, 0, 0, 0.2), 0 8px 32px rgba(102, 126, 234, 0.15)',
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        border: '1px solid rgba(0, 0, 0, 0.1)',
        zIndex: 2,
        maxHeight: '90vh',
        overflowY: 'auto',
        overflowX: 'hidden',
        boxSizing: 'border-box'
      }} onClick={e => e.stopPropagation()}>
        {/* 위 단추 줄 띠 — 창 맨 위 가로 전체에 사이트 보라(사용자 2026-10-27, 휴대폰과 같게). 단추는 아래 제목 줄에서 띠 위로 올라온다 */}
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 56, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }} />
        {/* 제목과 버튼들 */}
        <>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              // 보기에서는 제목이 위 보라 띠로 올라가 이 줄은 단추 자리만 (사용자 2026-10-28 "제목을 맨 위에다가 올려버리자")
              marginBottom: editMode ? 20 : 0,
              minHeight: editMode ? 40 : 0,
              position: 'relative',
              width: '100%'
            }}>
              {/* 제목 */}
              {editMode ? (
                <input
                  value={editTitle}
                  onChange={e => setEditTitle(e.target.value)}
                  style={{
                    flex: 1,
                    fontSize: 24,
                    fontWeight: 800,
                    background: '#fff',
                    border: '2px solid #667eea',
                    borderRadius: 12,
                    padding: '8px 12px',
                    color: '#000',
                    outline: 'none',
                    fontFamily: 'inherit',
                    marginRight: '140px',
                    boxShadow: '0 0 0 3px rgba(102, 126, 234, 0.1)'
                  }}
                  maxLength={40}
                  autoFocus
                />
              ) : (
                // 제목 — 위 보라 띠 안(단추 줄 왼쪽). 길면 말줄임, 마우스를 올리면 전체
                <div title={tactic.title} style={{
                  position: 'absolute',
                  left: -24,
                  right: 110,
                  top: -72,
                  height: 56,
                  display: 'flex',
                  alignItems: 'center',
                  fontSize: 19,
                  fontWeight: 800,
                  color: '#fff',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis'
                }}>
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{tactic.title}</span>
                </div>
              )}

              {/* 버튼들 */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: 3,
                position: 'absolute',
                // 창 안쪽 여백(48)만큼 비던 오른쪽을 줄인다 — 닫기 단추가 창 오른쪽 끝에서 16 안쪽(사용자 2026-10-27)
                right: -32,
                // 제목 줄 위 여백(보라 띠)으로 올린다 — 제목이 길면 단추와 겹쳤다(사용자 지시). 창 위 여백 72 · 띠 56 의 가운데
                top: -64
              }}>
                {/* 더보기 메뉴 */}
                {(isOwner || isAdmin) && (
                  <div style={{ position: 'relative', zIndex: 10 }} ref={dropdownRef}>
                    <button
                      onClick={() => setDropdownOpen(v => !v)}
                      style={{
                        width: 40,
                        height: 40,
                        borderRadius: 8,
                        background: 'transparent',
                        border: 'none',
                        fontSize: 18,
                        cursor: 'pointer',
                        color: '#fff',
                        padding: 0,
                        margin: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transition: 'all 0.3s ease'
                      }}
                      title="더보기"
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'transparent';
                      }}
                    >
                      <span style={{ fontSize: 20, fontWeight: 700, letterSpacing: 1, lineHeight: 1 }}>⋮</span>
                    </button>
                    {dropdownOpen && (
                      <div style={{
                        position: 'absolute',
                        top: 50,
                        right: 0,
                        background: 'rgba(255, 255, 255, 0.95)',
                        border: '1px solid rgba(255, 255, 255, 0.3)',
                        borderRadius: 12,
                        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.15)',
                        minWidth: 70,
                        zIndex: 100,
                        display: 'flex',
                        flexDirection: 'column',
                        backdropFilter: 'blur(20px)',
                        overflow: 'hidden'
                      }}>

                        {(isOwner || isAdmin) && (
                          <button style={{
                            padding: '12px 20px',
                            border: 'none',
                            background: 'none',
                            cursor: 'pointer',
                            fontSize: 15,
                            textAlign: 'left',
                            color: '#333',
                            fontWeight: 500,
                            transition: 'all 0.2s ease'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background = 'rgba(10, 37, 159, 0.1)';
                            e.currentTarget.style.color = '#667eea';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = 'none';
                            e.currentTarget.style.color = '#333';
                          }}
                          onClick={handleEditClick}>수정</button>
                        )}
                        {(isOwner || isAdmin) && (
                          <button style={{
                            padding: '12px 20px',
                            border: 'none',
                            background: 'none',
                            cursor: 'pointer',
                            fontSize: 15,
                            color: '#d32f2f',
                            textAlign: 'left',
                            fontWeight: 500,
                            transition: 'all 0.2s ease'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background = 'rgba(211, 47, 47, 0.1)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = 'none';
                          }}
                          onClick={handleDeleteTactic}>삭제</button>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* URL 복사 버튼 */}
                <button
                  onClick={() => {
                    const url = `${window.location.origin}/tactic/${tactic._id}`;
                    if (navigator.clipboard && navigator.clipboard.writeText) {
                      navigator.clipboard.writeText(url).then(() => {
                        alert('공략 URL이 복사되었습니다!');
                      }).catch(() => {
                        alert('URL 복사에 실패했습니다.');
                      });
                    } else {
                      // 대체 방법: 텍스트 선택 및 복사
                      const textArea = document.createElement('textarea');
                      textArea.value = url;
                      document.body.appendChild(textArea);
                      textArea.select();
                      try {
                        document.execCommand('copy');
                        alert('공략 URL이 복사되었습니다!');
                      } catch (err) {
                        alert('URL 복사에 실패했습니다.');
                      }
                      document.body.removeChild(textArea);
                    }
                  }}
                  style={{
                    width: 40,
                    height: 40,
                    borderRadius: 8,
                    background: 'transparent',
                    border: 'none',
                    fontSize: 16,
                    cursor: 'pointer',
                    color: '#fff',
                    padding: 0,
                    margin: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'all 0.3s ease'
                  }}
                  title="URL 복사"
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'transparent';
                  }}
                >
                  <IconLink />
                </button>

                {/* 닫기 버튼 */}
                <button
                  onClick={handleClose}
                  style={{
                    width: 40,
                    height: 40,
                    borderRadius: 8,
                    background: 'transparent',
                    border: 'none',
                    fontSize: 24,
                    color: '#fff',
                    cursor: 'pointer',
                    fontWeight: 700,
                    transition: 'all 0.3s ease',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'transparent';
                  }}
                >×</button>
              </div>
            </div>


            {/* 프로필/닉네임/작성일 */}
            <div style={{
              width: '100%',
              marginBottom: 28
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 20,
                width: '100%',
                background: '#fff',
                padding: '20px 0',
                borderBottom: '1px solid #e0e0e0'
              }}>
                {/* 프로필 이미지 + 닉네임/조회수 */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  {/* 네모난 프로필 이미지 */}
                  <img loading="lazy"
                    src={
                      tactic.profileImg || '/images/default-profile.png'
                    }
                    alt="프로필"
                    style={{
                      width: 48,
                      height: 48,
                      borderRadius: 8,
                      objectFit: 'cover',
                      border: '2px solid rgba(102, 126, 234, 0.3)',
                      background: '#fff'
                    }}
                    onError={(e) => { (e.target as HTMLImageElement).src = '/images/default-profile.png'; }}
                  />

                  {/* 닉네임과 조회수 */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <div style={{
                      fontSize: 18,
                      fontWeight: 700,
                      color: tactic.isDeleted ? '#ff6b6b' : '#1976d2',
                      textAlign: 'left'
                    }}>
                      {tactic.nickname}
                    </div>
                    <div style={{
                      fontSize: 14,
                      color: '#888',
                      fontWeight: 500,
                      textAlign: 'left'
                    }}>
                      조회수 {typeof tactic.views === 'number' ? tactic.views : 0}
                      {/* 작성일 — 휴대폰과 같게(사용자 2026-10-27) */}
                      {tactic.createdAt && ` · ${monthDayTime(tactic.createdAt, false)}`}
                    </div>
                  </div>
                </div>

              </div>
            </div>
          </>

        {/* 공덱/방덱 */}
        <div style={{
          width: '100%',
          marginBottom: 28,
          paddingBottom: 28,
          borderBottom: '1px solid #e0e0e0'
        }}>
          {editMode ? (
            <div style={{
              display: 'flex',
              flexDirection: 'row',
              gap: 48,
              width: '100%',
              justifyContent: 'center',
              alignItems: 'flex-start'
            }}>
              {/* 공덱 선택 */}
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
                  {editAttackTeam.map((monster, idx) => (
                    <div key={idx} className={tab === 'runes' && runeIdx === idx ? 'mob-picked' : undefined} style={{ position: 'relative', cursor: 'pointer', ...(tab === 'runes' ? { borderRadius: 10 } : {}) }} onClick={() => {
                      if (tab === 'runes') { setRuneIdx(idx); return; }
                      setEditingTeam('attack');
                      setEditingSlot(idx);
                      setMonsterSelectOpen(true);
                    }}>
                      {monster.name ? (
                        <>
                          <div style={{
                            width: 60,
                            height: 60,
                            border: '2px solid #1976d2',
                            borderRadius: 8,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            background: '#fff',
                            position: 'relative'
                          }}>
                            <img loading="lazy"
                              src={`/mobs/v3/${getMonsterImgPath(monsterMapping, monster).imgPath?.replace(/\\/g, '/')}`}
                              alt={monster.name}
                              style={{ width: 54, height: 54, objectFit: 'contain' }}
                            />
                          </div>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleMonsterRemove('attack', idx);
                            }}
                            style={{
                              position: 'absolute',
                              top: -6,
                              right: -6,
                              width: 20,
                              height: 20,
                              borderRadius: '50%',
                              background: '#f44336',
                              color: '#fff',
                              border: 'none',
                              fontSize: 12,
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}
                          >×</button>
                        </>
                      ) : (
                        <div style={{
                          width: 60,
                          height: 60,
                          border: '2px dashed #1976d2',
                          borderRadius: 8,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: '#1976d2',
                          fontSize: 24
                        }}>+</div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
              {/* 방덱 선택 */}
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
                  {editDefenseTeam.map((monster, idx) => (
                    <div key={idx} style={{ position: 'relative', cursor: 'pointer' }} onClick={() => {
                      setEditingTeam('defense');
                      setEditingSlot(idx);
                      setMonsterSelectOpen(true);
                    }}>
                      {monster.name ? (
                        <>
                          <div style={{
                            width: 60,
                            height: 60,
                            border: '2px solid #d32f2f',
                            borderRadius: 8,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            background: '#fff',
                            position: 'relative'
                          }}>
                            <img loading="lazy"
                              src={`/mobs/v3/${getMonsterImgPath(monsterMapping, monster).imgPath?.replace(/\\/g, '/')}`}
                              alt={monster.name}
                              style={{ width: 54, height: 54, objectFit: 'contain' }}
                            />
                          </div>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleMonsterRemove('defense', idx);
                            }}
                            style={{
                              position: 'absolute',
                              top: -6,
                              right: -6,
                              width: 20,
                              height: 20,
                              borderRadius: '50%',
                              background: '#f44336',
                              color: '#fff',
                              border: 'none',
                              fontSize: 12,
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}
                          >×</button>
                        </>
                      ) : (
                        <div style={{
                          width: 60,
                          height: 60,
                          border: '2px dashed #d32f2f',
                          borderRadius: 8,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: '#d32f2f',
                          fontSize: 24
                        }}>+</div>
                      )}
                    </div>
                  ))}
                </div>
                <ExtraDefenseRows rows={editExtra} mapping={monsterMapping} size={30} editable
                  onRemoveRow={(r) => setEditExtra((rows) => rows.filter((_, i) => i !== r))} />
                {/* 통계의 상대 방덱에서 가져오기 — 방덱 칸 아래(사용자) */}
                <div style={{ marginTop: 8, display: 'flex', justifyContent: 'center' }}>
                  <ImportDefenseButton rows={editExtra} onChange={setEditExtra} main={editDefenseTeam} onMain={setEditDefenseTeam} mapping={monsterMapping} isMobile={false} />
                </div>
              </div>
            </div>
          ) : (
            <div style={{
              display: 'flex',
              flexDirection: 'row',
              gap: 24,
              width: '100%',
              justifyContent: 'center',
              alignItems: 'flex-start'
            }}>
              {/* 공덱 */}
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
                <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap', justifyContent: 'center' }}>
                  {(tactic.attackTeam || []).map((m: any, idx: number) => {
                  const { imgPath, displayName } = getMonsterImgPath(monsterMapping, m);
                  return (
                    <div key={idx} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: '0 0 auto', gap: 4 }}>
                      {/* 공덱 몹을 누르면 [공략] 탭에서도 바로 [룬 정보]로 넘어가 그 몹 룬을 보인다 (사용자 2026-10-28) */}
                      <div onClick={() => { setRuneIdx(idx); setTab('runes'); }} title="이 몹의 룬 보기" className={tab === 'runes' && runeIdx === idx ? 'mob-picked' : undefined} style={{ position: 'relative', width: 60, height: 60, cursor: 'pointer', borderRadius: 12 }}>
                        {imgPath ? (
                          <span
                            style={{
                              display: 'inline-block',
                              width: 60,
                              height: 60,
                              borderRadius: 10,
                              padding: 0,
                              background: 'linear-gradient(270deg, #1976d2, #42a5f5, #90caf9, #1976d2)',
                              backgroundSize: '400% 400%',
                              animation: 'blue-border 3s linear infinite',
                              position: 'relative',
                            }}
                          >
                            <img loading="lazy"
                              src={`/mobs/v3/${imgPath.replace(/\\/g, '/')}`}
                              alt={displayName}
                              style={{
                                width: 54,
                                height: 54,
                                objectFit: 'contain',
                                borderRadius: 8,
                                margin: 3,
                                background: '#fff',
                                display: 'block',
                                boxShadow: '0 2px 8px #1976d222',
                                transition: 'opacity 0.3s',
                              }}
                            />
                          </span>
                        ) : (
                          <div style={{ width: 60, height: 60, background: '#eee', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 10, fontSize: 13, color: '#aaa', border: '1.5px solid #ddd' }}>
                            없음
                          </div>
                        )}
                      </div>
                      {imgPath ? (
                        <div style={{ fontSize: 11, color: '#333', fontWeight: 600, textAlign: 'center', maxWidth: 70, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{displayName}</div>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            </div>
            {/* VS — 몹 그림 높이의 가운데(사용자 2026-10-27, 휴대폰과 같게) */}
            <div style={{ height: 60, display: 'flex', alignItems: 'center', fontSize: 30, fontWeight: 900, color: '#667eea', letterSpacing: 1, flexShrink: 0 }}>VS</div>
            {/* 방덱 */}
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
              <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap', justifyContent: 'center' }}>
                {(tactic.defenseTeam || []).map((m: any, idx: number) => {
                  const { imgPath, displayName } = getMonsterImgPath(monsterMapping, m);
                  return (
                    <div key={idx} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: '0 0 auto', gap: 4 }}>
                      <div style={{ position: 'relative', width: 60, height: 60 }}>
                        {imgPath ? (
                          <span
                            style={{
                              display: 'inline-block',
                              width: 60,
                              height: 60,
                              borderRadius: 10,
                              padding: 0,
                              background: 'linear-gradient(270deg, #d32f2f, #ff5252, #ffb4b4, #d32f2f)',
                              backgroundSize: '400% 400%',
                              animation: 'red-border 3s linear infinite',
                              position: 'relative',
                            }}
                          >
                            <img loading="lazy"
                              src={`/mobs/v3/${imgPath.replace(/\\/g, '/')}`}
                              alt={displayName}
                              style={{
                                width: 54,
                                height: 54,
                                objectFit: 'contain',
                                borderRadius: 8,
                                margin: 3,
                                background: '#fff',
                                display: 'block',
                                boxShadow: '0 2px 8px #d32f2f22',
                                transition: 'opacity 1s',
                              }}
                            />
                          </span>
                        ) : (
                          <div style={{ width: 60, height: 60, background: '#eee', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 10, fontSize: 13, color: '#aaa', border: '1.5px solid #ddd' }}>
                            없음
                          </div>
                        )}
                      </div>
                      {imgPath ? (
                        <div style={{ fontSize: 11, color: '#333', fontWeight: 600, textAlign: 'center', maxWidth: 70, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{displayName}</div>
                      ) : null}
                    </div>
                  );
                })}
              </div>
              <ExtraDefenseRows rows={(tactic as any).extraDefenses} mapping={monsterMapping} size={30} editable={false} />
            </div>
          </div>
          )}
        </div>

        {/* [공략] / [룬 정보] 탭 — 수정 중에도 그대로 둔다(룬 설정은 [룬 정보] 탭에서 고친다) */}
        {(
          <div style={{ display: 'flex', gap: 6, width: '100%', marginBottom: 14, borderBottom: '1px solid #eee' }}>
            {([['tactic', '공략'], ['runes', '룬 정보']] as Array<['tactic' | 'runes', string]>).map(([k, label]) => (
              <button
                key={k} type="button"
                onClick={() => {
                  setTab(k);
                  // 룬 정보로 오면 세팅이 있는 첫 공덱 몹을 고른다
                  if (k === 'runes') {
                    const first = [0, 1, 2].find((i) => hasBuild(tactic.builds?.attack?.[i] as TacticBuild));
                    setRuneIdx(first ?? 0);
                  }
                }}
                style={{
                  padding: '10px 18px', border: 'none', background: 'transparent', cursor: 'pointer',
                  fontSize: 15, fontWeight: 800, color: tab === k ? '#667eea' : '#aaa',
                  borderBottom: tab === k ? '3px solid #667eea' : '3px solid transparent', marginBottom: -1
                }}
              >{label}</button>
            ))}
          </div>
        )}

        {tab === 'runes' && (
          <div style={{ width: '100%', marginBottom: 20 }}>
            {editMode ? (
              <>
                {/* 수정 중 — 고친 세팅은 게시글을 저장할 때 함께 저장된다. 위 공덱 칸을 누르면 몹이 바뀐다 */}
                <BuildRunes
                  build={editBuilds[runeIdx] || null}
                  mob={editAttackTeam[runeIdx]?.name ? editAttackTeam[runeIdx] : null}
                  mapping={monsterMapping}
                  isMobile={false}
                  onSave={(nb) => setEditBuilds((bs) => bs.map((x, i) => (i === runeIdx ? nb : x)))}
                  applyLabel="적용"
                />
                <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', alignItems: 'center', marginTop: 14 }}>
                  <button
                    type="button" disabled={editLoading}
                    onClick={() => { setEditMode(false); setEditError(''); }}
                    style={{ padding: '8px 20px', borderRadius: 12, border: '2px solid #667eea', background: 'rgba(102, 126, 234, 0.1)', color: '#667eea', fontWeight: 700, fontSize: 15, cursor: 'pointer' }}
                  >취소</button>
                  <button
                    type="button" disabled={editLoading}
                    onClick={handleEditSave}
                    style={{ padding: '8px 20px', borderRadius: 12, border: 'none', background: 'linear-gradient(135deg, #667eea, #764ba2)', color: '#fff', fontWeight: 700, fontSize: 15, cursor: 'pointer' }}
                  >저장</button>
                </div>
                {editError && <div style={{ color: '#d32f2f', marginTop: 8, fontSize: 13, fontWeight: 500, textAlign: 'right' }}>{editError}</div>}
              </>
            ) : (
              <>
                <BuildRunes
                  build={(tactic.builds?.attack?.[runeIdx] as TacticBuild) || null}
                  mob={(tactic.attackTeam || [])[runeIdx]}
                  mapping={monsterMapping}
                  isMobile={false}
                />
                {/* 룬 변경은 공략 [수정] 중에만 — 보기에서는 단추를 두지 않는다 (사용자 2026-10-28) */}
                {/* 내 룬으로 맞추기 — 이 공략 공덱 룬작에 내 룬을 룬 옵티마이저 [룬피팅] 탭에서 맞춘다 (사용자 2026-10-27) */}
                {fitButton}
              </>
            )}
          </div>
        )}

        {tab === 'tactic' && (<>
        {/* 게시글 본문 */}
        <div style={{ width: '100%', marginBottom: 32 }}>
          {editMode ? (
            <div style={{ position: 'relative', width: '100%' }}>
              <textarea
                value={editComment}
                onChange={e => setEditComment(e.target.value)}
                style={{
                  fontSize: 17,
                  color: '#333',
                  minHeight: 280,
                  width: '100%',
                  padding: '24px 28px',
                  resize: 'vertical',
                  transition: 'all 0.3s ease',
                  fontFamily: 'inherit',
                  lineHeight: 1.8,
                  borderRadius: 16,
                  border: '2px solid #e0e0e0',
                  background: '#fff'
                }}
                onFocus={(e) => {
                  e.currentTarget.style.borderColor = '#667eea';
                  e.currentTarget.style.boxShadow = '0 0 0 3px rgba(102, 126, 234, 0.1)';
                }}
                onBlur={(e) => {
                  e.currentTarget.style.borderColor = '#e0e0e0';
                  e.currentTarget.style.boxShadow = 'none';
                }}
                maxLength={1000}
              />
              {editError && <div style={{ color: '#d32f2f', marginTop: 12, fontSize: 14, fontWeight: 500 }}>{editError}</div>}
            </div>
          ) : (
            <div style={{
              background: '#fff',
              padding: '32px 0',
              fontSize: 17,
              color: '#333',
              lineHeight: 1.9,
              textAlign: 'left',
              minHeight: 280,
              width: '100%',
              whiteSpace: 'pre-line',
              maxHeight: `calc(1.9em * 16)`,
              overflowY: 'auto',
              borderBottom: '1px solid #e0e0e0'
            }}>
              {tactic.comment}
            </div>
          )}
        </div>

        {/* 저장/취소 버튼 */}
        {editMode && (
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginBottom: 32 }}>
            <button
              onClick={() => {
                setEditMode(false);
                setEditError('');
              }}
              disabled={editLoading}
              style={{
                background: 'rgba(102, 126, 234, 0.1)',
                color: '#667eea',
                border: '2px solid #667eea',
                borderRadius: 12,
                padding: '8px 20px',
                fontWeight: 700,
                fontSize: 15,
                cursor: 'pointer',
                opacity: editLoading ? 0.5 : 1,
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                if (!editLoading) {
                  e.currentTarget.style.background = 'rgba(102, 126, 234, 0.2)';
                  e.currentTarget.style.transform = 'translateY(-1px)';
                }
              }}
              onMouseLeave={(e) => {
                if (!editLoading) {
                  e.currentTarget.style.background = 'rgba(102, 126, 234, 0.1)';
                  e.currentTarget.style.transform = 'translateY(0)';
                }
              }}
            >취소</button>
            <button
              onClick={handleEditSave}
              disabled={editLoading}
              style={{
                background: 'linear-gradient(135deg, #667eea, #764ba2)',
                color: '#fff',
                border: 'none',
                borderRadius: 12,
                padding: '8px 20px',
                fontWeight: 700,
                fontSize: 15,
                cursor: 'pointer',
                opacity: editLoading ? 0.7 : 1,
                transition: 'all 0.3s ease',
                boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)'
              }}
              onMouseEnter={(e) => {
                if (!editLoading) {
                  e.currentTarget.style.transform = 'translateY(-1px)';
                  e.currentTarget.style.boxShadow = '0 6px 20px rgba(102, 126, 234, 0.4)';
                }
              }}
              onMouseLeave={(e) => {
                if (!editLoading) {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(102, 126, 234, 0.3)';
                }
              }}
            >저장</button>
          </div>
        )}
        {!editMode && fitButton}

        </>)}

      </div>
      {/* 몬스터 선택 모달 */}
      {monsterSelectOpen && (
        <MonsterSelect
          open={monsterSelectOpen}
          monsterMapping={monsterMapping}
          onClose={() => setMonsterSelectOpen(false)}
          onSelect={handleMonsterSelect}
        />
      )}
    </div>
  );
};

export default TacticPost;
